import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { CodeMirrorInput } from 'browser/components/atomic-elements/atoms/input/code-mirror-input'
import { EntitySelectField } from 'browser/components/atomic-elements/molecules/fields/select-field/entity-select-field'
import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import { EntityPreviewCard } from 'browser/components/atomic-elements/organisms/entity/entity-preview-card/entity-preview-card'
import { JSONSelectFactory } from 'browser/components/json-elements/higher-order-components/json-select-factory'
import ComponentsMap from 'browser/components'
import { MultiMixinRenderer } from 'shared-libs/components/entity/multi-mixin-renderer'
import { Entity } from 'shared-libs/models/entity'

const ENTITY_DETAIL_UI_SCHEMA_PATH = 'uiSchema.web.entityDetailSection'

const UIEntitySelectField = JSONSelectFactory(EntitySelectField)
const UISelectField = JSONSelectFactory(SelectField)

const PAYLOAD_CONFIG_PATH = 'core_integrations_ingestion_multiEntity_payloadConfig'
const SFTP_CONFIG_ENTITY_TYPE =
  '/1.0/entities/metadata/core_integrations_sftp_ingestion_config.json'
const CSV_IMPORT_ENTITY_TYPE = '/1.0/entities/metadata/csvImport.json'

const SOURCE_PASTE = 'PASTE'
const SOURCE_REPLAY = 'REPLAY'
const SOURCE_SFTP_LATEST = 'SFTP_LATEST'
const SOURCE_OPTIONS = [
  { label: 'Paste sample', value: SOURCE_PASTE },
  { label: 'Replay a past drop', value: SOURCE_REPLAY },
  { label: 'Pull latest from SFTP', value: SOURCE_SFTP_LATEST },
]

interface ITestPayloadConfigProps {
  entity: any
}

interface ITestPayloadConfigState {
  configEdge: any
  source: string
  sample: string
  csvImportEdge: any
  runPreProcessor: boolean
  isRunning: boolean
  result: any
  error: string
}

/** Keeps a failed entity preview (e.g. an unresolved schema) from taking down the whole result list. */
class ResultErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }
  public static getDerivedStateFromError() {
    return { hasError: true }
  }
  public render() {
    if (this.state.hasError) {
      return <div className='c-placeholderColor'>(entity preview unavailable — see raw JSON)</div>
    }
    return this.props.children
  }
}

/**
 * Renders a dry-run result entity with the real metadata-driven detail renderer instead of raw JSON.
 *
 * The result entities are transient (never persisted), so a bare `new Entity(content, apis)` has no
 * resolved mixin schemas and the renderer would draw nothing. We resolve the mixin metadata through
 * the store first (async, read-only — `shouldCache=false` keeps this preview out of the identity
 * map), then render `uiSchema.web.entityDetailSection` via MultiMixinRenderer, which renders empty
 * (rather than throwing) for any mixin that doesn't define that path.
 */
class ResolvedEntityDetail extends React.Component<
  { content: any },
  { model: Entity | null; status: 'loading' | 'ready' | 'failed' }
> {
  private isMounted_ = false

  constructor(props) {
    super(props)
    this.state = { model: null, status: 'loading' }
  }

  public componentDidMount() {
    this.isMounted_ = true
    apis
      .getStore()
      .resolveRecords([_.cloneDeep(this.props.content)], /* shouldCache */ false)
      .then(([model]) => {
        if (this.isMounted_) {
          this.setState({ model, status: 'ready' })
        }
      })
      .catch(() => {
        if (this.isMounted_) {
          this.setState({ status: 'failed' })
        }
      })
  }

  public componentWillUnmount() {
    this.isMounted_ = false
  }

  public render() {
    const { model, status } = this.state
    if (status === 'loading') {
      return <div className='c-placeholderColor u-bumperTop--xs'>Rendering entity…</div>
    }
    if (status === 'failed' || !model) {
      // Fall back silently — the collapsed raw JSON is still available below.
      return null
    }
    return (
      <div
        className='u-bumperTop--xs'
        style={{ border: '1px solid #e0e0e0', borderRadius: 3, padding: 8 }}
      >
        <MultiMixinRenderer
          value={model}
          componentsMap={ComponentsMap}
          uiSchemaPath={ENTITY_DETAIL_UI_SCHEMA_PATH}
          // Render only the most-derived mixin's detail section, so an entity with several mixins
          // that each define entityDetailSection (e.g. carrierAppointment = calendar appointment +
          // carrier appointment) shows one consolidated section instead of a repeated one.
          shouldOverrideUiSchema={true}
          context={{ density: 'collapse', isHorizontalLayout: true, isStatic: true }}
          state={{ settings: apis.getSettings() }}
          actions={this}
          isEditable={false}
          onChangeComplete={_.noop}
        />
      </div>
    )
  }
}

/** Builds a lightweight (unresolved) Entity model for the preview-card title; null on failure. */
function toEntityModel(content: any): Entity | null {
  if (!content) {
    return null
  }
  try {
    return new Entity(content, apis)
  } catch {
    return null
  }
}

function tryParseJson(text: any): any {
  if (typeof text !== 'string') {
    return undefined
  }
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/**
 * Pulls the server's error detail out of a rejected request. The API layer merges the parsed
 * response body ({ statusCode, errors: [{ message }] }) onto the Error, so the useful message lives
 * in `err.errors` (worker path) or `err.responseJSON` / `err.responseText` (jQuery path) — not in
 * `err.message`, which is just "HTTP error 400".
 */
function errorDetail(err: any): string | undefined {
  const fromErrors = (arr: any): string | undefined =>
    Array.isArray(arr) && arr.length
      ? arr.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean).join('\n')
      : undefined
  const parsed = tryParseJson(err?.responseText)
  return (
    fromErrors(err?.errors) ||
    fromErrors(err?.responseJSON?.errors) ||
    fromErrors(parsed?.errors) ||
    (typeof err?.responseText === 'string' && err.responseText.trim() ? err.responseText : undefined)
  )
}

/** Combines the HTTP status header with the server's detail message for display. */
function formatRunError(err: any): string {
  const status = err?.statusCode ?? err?.status
  const header = err?.message || (status ? `HTTP error ${status}` : 'Test run failed.')
  const detail = errorDetail(err)
  return detail && detail !== header ? `${header}\n${detail}` : header
}

/**
 * One dry-run result entity rendered as a collapsible card: a header (caret + alias chip + action +
 * valid/invalid) that toggles the body (metadata detail render + raw JSON). Collapsible because a
 * single entity's detail can be long, and a row can produce several.
 */
class AliasResultCard extends React.Component<
  { aliasResult: any },
  { collapsed: boolean; detailCollapsed: boolean; rawJsonCollapsed: boolean }
> {
  constructor(props) {
    super(props)
    this.state = { collapsed: false, detailCollapsed: true, rawJsonCollapsed: true }
  }

  private toggle = () => this.setState((prev) => ({ collapsed: !prev.collapsed }))

  private toggleDetail = () => this.setState((prev) => ({ detailCollapsed: !prev.detailCollapsed }))

  private toggleRawJson = () =>
    this.setState((prev) => ({ rawJsonCollapsed: !prev.rawJsonCollapsed }))

  public render() {
    const { aliasResult } = this.props
    const { collapsed, detailCollapsed, rawJsonCollapsed } = this.state
    const validationErrors = aliasResult.validationErrors || []
    const entityModel = toEntityModel(aliasResult.entity)
    const valid = aliasResult.valid
    return (
      <div
        className='u-bumperTop--sm'
        style={{ border: '1px solid #d0d5dd', borderRadius: 4, overflow: 'hidden' }}
      >
        <div
          className='u-flex u-alignItems--center'
          style={{
            background: '#f4f6f8',
            padding: '6px 10px',
            borderBottom: collapsed ? 'none' : '1px solid #e0e0e0',
            cursor: 'pointer',
          }}
          onClick={this.toggle}
        >
          <Icon
            icon={collapsed ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_DOWN}
            className='c-placeholderColor u-bumperRight--xs'
          />
          <span
            style={{
              fontFamily: 'monospace',
              fontWeight: 600,
              background: '#e6edf7',
              color: '#1c4587',
              padding: '1px 8px',
              borderRadius: 10,
            }}
          >
            {aliasResult.alias}
          </span>
          <span className='u-bumperLeft--xs' style={{ fontWeight: 600 }}>
            {aliasResult.action}
          </span>
          <span
            className={valid ? undefined : 'c-errorColor'}
            style={{ marginLeft: 'auto', fontWeight: 600, color: valid ? '#0f7b3f' : undefined }}
          >
            {valid ? '✓ valid' : '✗ invalid'}
          </span>
        </div>
        {!collapsed && (
          <div style={{ padding: 8 }}>
            {validationErrors.length > 0 && (
              <ul className='c-errorColor'>
                {validationErrors.map((err, i) => (
                  <li key={i}>
                    {err.field ? `${err.field}: ` : ''}
                    {err.message}
                  </li>
                ))}
              </ul>
            )}
            {entityModel && (
              <ResultErrorBoundary>
                <EntityPreviewCard entity={entityModel} isClickable={false} />
              </ResultErrorBoundary>
            )}
            {aliasResult.entity && (
              <div className='u-bumperTop--xs'>
                <div
                  className='u-flex u-alignItems--center'
                  style={{ cursor: 'pointer', fontWeight: 600 }}
                  onClick={this.toggleDetail}
                >
                  <Icon
                    icon={detailCollapsed ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_DOWN}
                    className='c-placeholderColor u-bumperRight--xs'
                  />
                  Entity detail
                </div>
                {!detailCollapsed && (
                  <ResultErrorBoundary>
                    <ResolvedEntityDetail content={aliasResult.entity} />
                  </ResultErrorBoundary>
                )}
              </div>
            )}
            <div className='u-bumperTop--xs'>
              <div
                className='u-flex u-alignItems--center'
                style={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={this.toggleRawJson}
              >
                <Icon
                  icon={rawJsonCollapsed ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_DOWN}
                  className='c-placeholderColor u-bumperRight--xs'
                />
                raw JSON
              </div>
              {!rawJsonCollapsed && (
                <CodeMirrorInput
                  value={JSON.stringify(aliasResult.entity, null, 2)}
                  language='json'
                  onChange={_.noop}
                />
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
}

/**
 * Dry-run sandbox for a Multi Entity Ingestion Payload Config. Runs the draft config (as currently
 * edited, unsaved) against one of three input sources — a pasted sample, a past drop (CsvImport) for
 * the chosen SFTP config, or the latest file pulled read-only from SFTP — through the real ingestion
 * loop without writing anything, and shows, per row, the entities the config would produce, their
 * create/update action, and validation, each rendered as an entity preview alongside its raw JSON.
 *
 * Placement-agnostic: rendered as the "Dry Run" tab on the payload config's detail card. Gating
 * (super-admin + payload-config mixin) is applied by the caller.
 */
export class TestPayloadConfig extends React.Component<
  ITestPayloadConfigProps,
  ITestPayloadConfigState
> {
  constructor(props: ITestPayloadConfigProps) {
    super(props)
    this.state = {
      configEdge: null,
      source: SOURCE_PASTE,
      sample: '',
      csvImportEdge: null,
      runPreProcessor: true,
      isRunning: false,
      result: null,
      error: '',
    }
  }

  public render() {
    const { source } = this.state
    return (
      <div className='u-innerBumper'>
        <p className='c-placeholderColor u-bumperBottom--sm'>
          Dry run: nothing is written. Shows the entities each input row would produce.
        </p>

        <UIEntitySelectField
          label='SFTP Ingestion Config'
          entityType={SFTP_CONFIG_ENTITY_TYPE}
          value={this.state.configEdge}
          onChange={(edge) => this.setState({ configEdge: edge })}
        />

        <UISelectField
          label='Input source'
          enumOptions={SOURCE_OPTIONS}
          optionLabelPath='label'
          optionValuePath='value'
          value={source}
          onChange={(value) => this.setState({ source: _.isString(value) ? value : value?.value })}
        />

        {this.renderSourceInput()}

        <Button isDisabled={!this.canRun()} onClick={this.handleRun}>
          {this.state.isRunning ? 'Running…' : 'Run'}
        </Button>

        {this.renderResult()}
      </div>
    )
  }

  private renderSourceInput() {
    const { source, sample, csvImportEdge } = this.state
    const configId = this.getConfigId()

    if (source === SOURCE_REPLAY) {
      return (
        <div className='u-bumperTop--sm u-bumperBottom--sm'>
          {configId ? (
            <UIEntitySelectField
              label='Past drop to replay'
              entityType={CSV_IMPORT_ENTITY_TYPE}
              filters={[{ type: 'match', path: 'creationSource.entityId', value: configId }]}
              orders={[{ path: 'creationDate', type: 'descending' }]}
              value={csvImportEdge}
              onChange={(edge) => this.setState({ csvImportEdge: edge })}
            />
          ) : (
            <div className='c-placeholderColor'>Select an SFTP Ingestion Config first.</div>
          )}
          <div className='c-placeholderColor u-bumperTop--xs'>
            The stored file was already munged at ingestion time, so this tests the entity, merge, and
            dedupe rules against real rows (the pre-processor is not re-run).
          </div>
        </div>
      )
    }

    if (source === SOURCE_SFTP_LATEST) {
      return (
        <div className='u-bumperTop--sm u-bumperBottom--sm'>
          <div className='c-placeholderColor u-bumperBottom--xs'>
            Reads a copy of the newest file in the configured SFTP source directory (read-only — the
            real ingestion job is unaffected) and runs it end to end.
          </div>
          {this.renderRunPreProcessor()}
        </div>
      )
    }

    // PASTE
    return (
      <div className='u-bumperTop--sm u-bumperBottom--sm'>
        <div className='u-bumperBottom--xs'>
          Sample payload (CSV: header of alias.path columns, then data rows)
        </div>
        <CodeMirrorInput
          value={sample}
          placeholder={'appt.name,appt.trailer\nA123,TRL-9'}
          onChange={(value) => this.setState({ sample: value })}
        />
        <div className='u-bumperTop--xs'>{this.renderRunPreProcessor()}</div>
      </div>
    )
  }

  private renderRunPreProcessor() {
    return (
      <label className='u-flex u-alignItems--center'>
        <input
          type='checkbox'
          checked={this.state.runPreProcessor}
          onChange={(e) => this.setState({ runPreProcessor: e.target.checked })}
        />
        <span className='u-bumperLeft--xs'>
          Run munging pre-processor (needed to test the property map)
        </span>
      </label>
    )
  }

  private canRun(): boolean {
    const { source, sample, isRunning } = this.state
    if (isRunning || !this.getConfigId()) {
      return false
    }
    if (source === SOURCE_PASTE) {
      return _.trim(sample).length > 0
    }
    if (source === SOURCE_REPLAY) {
      return !!this.getCsvImportId()
    }
    return true
  }

  private handleRun = () => {
    const { entity } = this.props
    const { source, sample, runPreProcessor } = this.state
    const ingestionConfigId = this.getConfigId()
    if (!ingestionConfigId) {
      return
    }
    const payloadConfig = entity.get(PAYLOAD_CONFIG_PATH) ?? _.get(entity.content, PAYLOAD_CONFIG_PATH)

    this.setState({ isRunning: true, result: null, error: '' })

    apis
      .testIngestionPayloadConfig({
        ingestionConfigId,
        payloadConfig,
        source,
        sample: source === SOURCE_PASTE ? sample : undefined,
        csvImportId: source === SOURCE_REPLAY ? this.getCsvImportId() : undefined,
        runPreProcessor,
      })
      .then((result) => {
        const parsed = _.isString(result) ? JSON.parse(result) : result
        this.setState({ isRunning: false, result: parsed })
      })
      .catch((err) => {
        this.setState({ isRunning: false, error: formatRunError(err) })
      })
  }

  private getConfigId(): string | undefined {
    return this.edgeId(this.state.configEdge)
  }

  private getCsvImportId(): string | undefined {
    return this.edgeId(this.state.csvImportEdge)
  }

  private edgeId(value: any): string | undefined {
    const edge = _.isArray(value) ? value[0] : value
    return edge?.entityId
  }

  private renderResult() {
    const { result, error } = this.state
    if (error) {
      return <pre className='u-bumperTop c-errorColor'>{error}</pre>
    }
    if (!result) {
      return null
    }
    return (
      <div className='u-bumperTop'>
        <div className='u-bumperBottom--sm'>
          {result.rowCount} row(s), {result.failureCount} failed.
        </div>
        {(result.rows || []).map((row) => this.renderRow(row))}
      </div>
    )
  }

  private renderRow(row: any) {
    const entityCount = (row.entities || []).length
    return (
      <div
        key={row.rowNum}
        className='u-bumperBottom'
        style={{ borderTop: '2px solid #c0c6ce', paddingTop: 10, marginTop: 10 }}
      >
        <div className='u-bumperBottom--xs'>
          <strong>Row {row.rowNum}</strong>{' '}
          <span className='c-placeholderColor'>
            — {row.status} · {entityCount} {entityCount === 1 ? 'entity' : 'entities'}
          </span>
        </div>
        {(row.entities || []).map((aliasResult: any) => (
          <AliasResultCard key={aliasResult.alias} aliasResult={aliasResult} />
        ))}
        {this.renderSkips(row.skips)}
        {this.renderErrors(row.errors)}
      </div>
    )
  }

  private renderSkips(skips: any) {
    const entries = _.toPairs(skips || {})
    if (entries.length === 0) {
      return null
    }
    return (
      <ul className='u-bumperLeft--sm c-placeholderColor'>
        {entries.map(([alias, message]) => (
          <li key={alias}>
            {alias}: {message as string}
          </li>
        ))}
      </ul>
    )
  }

  private renderErrors(errors: any[]) {
    if (!errors || errors.length === 0) {
      return null
    }
    return (
      <ul className='u-bumperLeft--sm c-errorColor'>
        {errors.map((err, i) => (
          <li key={i}>{err.message}</li>
        ))}
      </ul>
    )
  }
}
